import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Line } from "react-chartjs-2";
import FETCH from "../../../utils/fetch";
import { exportElementToPaginatedPdf } from "../../../utils/pdfExport";
import { chartColors } from "../../../components/form/debtPayOff/components/color";
import { MODULE_API } from "../../../apis";
import getFAClientName from "../../../helpers/getFAClientName";
import { InsurancePDFReport } from "../components/InsurancePDFReport";

export const InsuranceReport = React.forwardRef((props, ref) => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [insuranceList, setInsuranceList] = useState([]);
  const [stats, setStats] = useState({});
  const [totalPremium, setTotalPremium] = useState(0);
  const [chartData, setChartData] = useState(null);
  const [clientName, setClientName] = useState("");
  const [pdfChartImage, setPdfChartImage] = useState(null);
  const chartRef = useRef(null);
  const hiddenChartContainerRef = useRef(null);
  const resolvedClientName = clientName || localStorage.getItem("User") || "";

  React.useImperativeHandle(ref, () => ({
    downloadPDF: () => {
      handleDownloadPDF();
    },
    isDownloading: downloadLoading,
  }));

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const userRole = localStorage.getItem("role") != "undefined" && JSON.parse(localStorage.getItem("role"));
      const ID = localStorage.getItem("accessId");
      
      // 1. Fetch Stats & Sum
      const [statsRes, sumRes] = await Promise.all([
        MODULE_API.fetchInsuranceStats(),
        MODULE_API.fetchInsuranceSum()
      ]);

      if (statsRes && statsRes.data) setStats(statsRes.data);
      if (sumRes && sumRes.data && sumRes.data.totalPremium) setTotalPremium(sumRes.data.totalPremium);

      // 2. Fetch All Insurance Records (Paging)
      let allRecords = [];
      let page = 1;
      let hasMore = true;
      const limit = 50; 

      while (hasMore) {
        const datares = await FETCH.post({
          url: "client-module-service/client-module/list",
          id: userRole === "Financial Advisor Client"
              ? `?module=Insurance Products V2&page=${page}&limit=${limit}&clientId=${ID}`
              : `?module=Insurance Products V2&page=${page}&limit=${limit}`,
          body: {},
        });

        if (datares && datares.records && datares.records.length > 0) {
          allRecords = [...allRecords, ...datares.records];
          if (datares.records.length < limit) hasMore = false;
          else page++;
        } else {
          hasMore = false;
        }
      }
      setInsuranceList(allRecords);

      // 3. Fetch Chart Data
      await fetchChartData();

      setLoading(false);
    } catch (error) {
      console.error("Error fetching insurance report data:", error);
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    const chartRes = await MODULE_API.fetchInsuranceChart();
    if (chartRes && chartRes.data) {
      setChartData(chartRes.data);
      return chartRes.data;
    }
    setChartData(null);
    return null;
  };

  const hasUsableChartData = (data) => {
    if (!data || typeof data !== "object") return false;

    return Object.values(data).some(
      (entries) => Array.isArray(entries) && entries.length > 0
    );
  };

  const waitForRender = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

  const getChartInstance = () => {
    const chartRefValue = chartRef.current;

    if (!chartRefValue) return null;
    if (chartRefValue.chartInstance) return chartRefValue.chartInstance;
    if (typeof chartRefValue.getChart === "function") return chartRefValue.getChart();
    if (typeof chartRefValue.toBase64Image === "function") return chartRefValue;

    return null;
  };

  useEffect(() => {
    fetchAllData();
    const name = getFAClientName();
    if (name) setClientName(name);
  }, []);

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);
      if (props.onDownloadLoading) props.onDownloadLoading(true);

      let latestChartData = chartData;
      if (!hasUsableChartData(latestChartData)) {
        latestChartData = await fetchChartData();
      }

      await waitForRender();

      // Capture the hidden chart rendered by this component.
      let chartImage = null;
      for (let i = 0; i < 30; i++) {
        const liveCanvas =
          hiddenChartContainerRef.current &&
          hiddenChartContainerRef.current.querySelector("canvas");
        if (liveCanvas) {
          chartImage = liveCanvas.toDataURL("image/png");
        }

        if (!chartImage || chartImage.length < 5000) {
          const chart = getChartInstance();
          if (chart) {
            chartImage = chart.toBase64Image();
          }
        }

        if (chartImage && chartImage.length > 5000) break;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      setPdfChartImage(chartImage && chartImage.length > 5000 ? chartImage : null);
      await waitForRender();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const element = document.getElementById("insurance-report-pdf-container");
      if (!element) {
        setDownloadLoading(false);
        if (props.onDownloadLoading) props.onDownloadLoading(false);
        return;
      }

      await exportElementToPaginatedPdf({
        element,
        fileName: `Insurance_Report_${moment().format("YYYYMMDD")}.pdf`,
        margin: 12,
      });

      setDownloadLoading(false);
      if (props.onDownloadLoading) props.onDownloadLoading(false);
    } catch (error) {
      console.error("Error generating insurance PDF:", error);
      setDownloadLoading(false);
      if (props.onDownloadLoading) props.onDownloadLoading(false);
    }
  };

  // Chart logic (mirrored from InsuranceInfo.jsx)
  const allPolicies = chartData && new Set();
  chartData && Object.values(chartData).forEach((entries) => {
    entries && entries.forEach((entry) => {
      Object.keys(entry).forEach((key) => allPolicies && allPolicies.add(key));
    });
  });

  const sortedMonths = chartData && Object.keys(chartData).sort((a, b) => 
    moment(a, "MMMM YYYY").toDate() - moment(b, "MMMM YYYY").toDate()
  );

  const chartLabels = sortedMonths && sortedMonths.map((label) => moment(label, "MMMM YYYY").format("MMM"));

  const datasets = allPolicies && Array.from(allPolicies).map((policyName, index) => ({
    type: "bar",
    label: policyName,
    stack: "stack1",
    backgroundColor: chartColors[index % chartColors.length],
    data: sortedMonths && sortedMonths.map((month) => {
      const entries = chartData[month];
      const entry = entries && entries.find((e) => policyName in e);
      return entry ? Number(parseFloat(entry[policyName]).toFixed(2)) : 0;
    }),
  }));

  const data = { labels: chartLabels || [], datasets: datasets || [] };

  return (
    <React.Fragment>
      {/* Hidden Chart for Capture */}
      <div
        ref={hiddenChartContainerRef}
        style={{ position: "fixed", top: 0, left: "-5000px", width: "950px", zIndex: -9999, opacity: 0.01, pointerEvents: "none" }}
      >
        <div style={{ padding: "40px", backgroundColor: "#fff" }}>
          <Line
            ref={chartRef}
            data={data}
            height={350}
            options={{
              animation: { duration: 0 },
              legend: { display: true, position: 'bottom' },
              scales: {
                xAxes: [{ stacked: true }],
                yAxes: [{ 
                  stacked: true,
                  ticks: {
                    beginAtZero: true,
                    callback: (value) => `$${value.toLocaleString()}`
                  }
                }],
              },
            }}
          />
        </div>
      </div>

      {/* Main PDF Template Container */}
      <div style={{ position: "fixed", top: 0, left: "-5000px", width: "1000px", zIndex: -9999, opacity: 0.01, pointerEvents: "none", overflow: "hidden" }}>
        <div id="insurance-report-pdf-container">
          <InsurancePDFReport
            insuranceList={insuranceList}
            totalPremium={totalPremium}
            stats={stats}
            clientName={resolvedClientName}
            chartImage={pdfChartImage}
          />
        </div>
      </div>
    </React.Fragment>
  );
});
